import React, { useRef, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import './styles.scss'
import ChatInput from '../../atoms/ChatInput'
import Chip from '../../atoms/Chip'
import PromptPill from '../../atoms/PromptPill'
import Button from '../../atoms/Button'
import FileDropZone from 'components/molecules/FileDropZone'
import DocumentUploadModal from 'components/molecules/DocumentUploadModal'
import {
  DocumentUploadResponse,
  DocumentUploadVariables,
  DOCUMENT_UPLOAD
} from 'share/mutations/documents'
import {
  GET_DEFAULT_DOCUMENT_CATEGORYID,
  GetDefaultDocumentCategoryIdResponse
} from 'share/queries/documents'
import { getMimeType } from 'share/utils/fileUtils'

import { useAppSelector } from 'hooks/store'

// Upload limits constants
const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25MB per file
const MAX_TOTAL_SIZE = 95 * 1024 * 1024 // 95MB total for batch upload
const MAX_FILES_COUNT = 50 // Maximum 50 files per batch

interface InputZoneProps {
  sendMessage?: (
    message: string,
    documents?: Array<{ document_name: string; document_id: string }>
  ) => void
  prompts?: string[]
  /** Whether the input should be disabled (e.g., during message streaming) */
  isDisabled?: boolean
}

const InputZone = ({
  sendMessage,
  prompts,
  isDisabled = false
}: InputZoneProps): JSX.Element => {
  const [value, setValue] = useState<string>('')
  const [pendingFiles, setPendingFiles] = useState<File[]>([]) // Files waiting to be uploaded
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]) // Track which files are uploading
  const [uploadErrors, setUploadErrors] = useState<string[]>([]) // Track upload errors
  const [showDocumentModal, setShowDocumentModal] = useState<boolean>(false)
  const [uploadFileMutation] =
    useMutation<DocumentUploadResponse, DocumentUploadVariables>(
      DOCUMENT_UPLOAD
    )

  // Fetch default document category id to use for uploads
  const { data: defaultCategoryData } =
    useQuery<GetDefaultDocumentCategoryIdResponse>(
      GET_DEFAULT_DOCUMENT_CATEGORYID
    )

  const activeCase = useAppSelector((state) => state.activeCase)

  const uploadDocuments = async () => {
    if (pendingFiles.length === 0) {
      console.error('No files selected for upload')
      return
    }

    const uploadedDocumentIds = []
    const currentUploadingFiles: string[] = []
    const currentErrors: string[] = []

    // Clear previous errors
    setUploadErrors([])

    for (const file of pendingFiles) {
      try {
        // Add file to uploading list
        currentUploadingFiles.push(file.name)
        setUploadingFiles([...currentUploadingFiles])

        console.log(`Starting upload for: ${file.name}`)

        // Sanitize filename to prevent issues with spaces, special characters, and double extensions
        let sanitizedName = file.name

        // Remove double extensions
        if (sanitizedName.endsWith('.pdf.pdf')) {
          sanitizedName = sanitizedName.replace('.pdf.pdf', '.pdf')
        }

        // Replace spaces and problematic characters with underscores
        sanitizedName = sanitizedName
          .replace(/[\s]+/g, '_') // Replace spaces
          .replace(/[^\w\-_.]/g, '_') // Replace special chars except dash, underscore, dot
          .replace(/_+/g, '_') // Collapse multiple underscores
          .replace(/^_+|_+$/g, '') // Remove leading/trailing underscores

        // Ensure filename isn't too long (max 100 chars before extension)
        const extension = sanitizedName.substring(
          sanitizedName.lastIndexOf('.')
        )
        const nameWithoutExt = sanitizedName.substring(
          0,
          sanitizedName.lastIndexOf('.')
        )
        if (nameWithoutExt.length > 100) {
          sanitizedName = nameWithoutExt.substring(0, 100) + extension
        }

        const sanitizedFile =
          sanitizedName !== file.name
            ? new File([file], sanitizedName, {
                type: file.type,
                lastModified: file.lastModified
              })
            : file

        console.log(`Sanitized filename: ${file.name} -> ${sanitizedFile.name}`)

        // Additional validation for problematic files
        if (file.name.includes('VV-Bank') || file.name.includes('Statement')) {
          console.log('Special validation for bank statement:', {
            originalName: file.name,
            sanitizedName: sanitizedFile.name,
            fileSize: file.size,
            fileType: file.type,
            lastModified: new Date(file.lastModified).toISOString(),
            browserInfo: {
              userAgent: navigator.userAgent,
              onLine: navigator.onLine,
              language: navigator.language
            }
          })

          // Check PDF structure more thoroughly
          const reader = new FileReader()
          reader.onload = (e) => {
            const arrayBuffer = e.target?.result as ArrayBuffer
            if (arrayBuffer) {
              const bytes = new Uint8Array(arrayBuffer)
              const header = Array.from(bytes.slice(0, 20))
                .map((b) => String.fromCharCode(b))
                .join('')

              // Look for PDF structure indicators
              const pdfContent = new TextDecoder().decode(bytes)
              const hasXref = pdfContent.includes('xref')
              const hasTrailer = pdfContent.includes('trailer')
              const hasEOF = pdfContent.includes('%%EOF')

              console.log('PDF structure analysis:', {
                header: header.substring(0, 10),
                isValidPDF: header.startsWith('%PDF-'),
                pdfVersion: header.substring(0, 8),
                hasXref,
                hasTrailer,
                hasEOF,
                firstBytes: Array.from(bytes.slice(0, 20)),
                lastBytes: Array.from(bytes.slice(-20)),
                containsSuspiciousContent: {
                  hasJavaScript: pdfContent
                    .toLowerCase()
                    .includes('javascript'),
                  hasEmbeddedFiles: pdfContent
                    .toLowerCase()
                    .includes('/embeddedfile'),
                  hasFormFields: pdfContent.toLowerCase().includes('/acroform')
                }
              })
            }
          }
          reader.readAsArrayBuffer(file)
        }

        let data
        try {
          const result = await uploadFileMutation({
            variables: {
              file: sanitizedFile,
              fileMetadata: {
                caseId: activeCase.matter?.id ?? 0,
                categoryId:
                  defaultCategoryData?.documents?.defaultCategoryDocumentFolder
                    ?.categoryId ??
                  (process.env.REACT_APP_ENV === 'dev' ? 67 : 1),
                documentType: file?.type || getMimeType(file?.name),
                replacedDocumentId: null,
                businessObjectId: 0,
                businessObjectType: ''
              }
            },
            context: {
              fetchOptions: {
                timeout: 60000 // 60 second timeout for large files
              }
            }
          })

          data = result.data

          console.log('Upload mutation completed:', {
            fileName: sanitizedFile.name,
            hasData: !!data,
            success: data?.documents?.uploadFile?.success
          })
        } catch (mutationError) {
          console.error('Upload mutation failed:', {
            fileName: sanitizedFile.name,
            error: mutationError,
            errorMessage:
              mutationError instanceof Error
                ? mutationError.message
                : 'Unknown error',
            errorName:
              mutationError instanceof Error ? mutationError.name : 'Unknown',
            stack:
              mutationError instanceof Error ? mutationError.stack : undefined
          })

          // Re-throw to be caught by the outer catch block
          throw mutationError
        }

        if (data?.documents.uploadFile.success) {
          uploadedDocumentIds.push({
            document_name: file.name,
            document_id:
              data.documents.uploadFile.uploadedDocument.id.toString()
          })
          console.log('Data Documents:', data?.documents.uploadFile)
          console.log(`Successfully uploaded: ${file.name}`)
        } else {
          const errorMsg = `Failed to upload document: ${file.name}`
          currentErrors.push(errorMsg)
          console.error(errorMsg)
        }
      } catch (error) {
        const errorMsg = `Error uploading ${file.name}: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
        currentErrors.push(errorMsg)

        // Detailed error analysis
        const errorDetails = {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          caseId: activeCase.matter?.id,
          error: error,
          errorType:
            error instanceof Error ? error.constructor.name : 'Unknown',
          stack: error instanceof Error ? error.stack : undefined,
          likelyIssue: 'Unknown'
        }

        // Check if it's an Apollo Error with specific details
        if (error && typeof error === 'object' && 'networkError' in error) {
          const apolloError = error as {
            networkError?: {
              message: string
              name: string
              statusCode?: number
              response?: unknown
            }
            graphQLErrors?: Array<{
              message: string
              locations?: unknown
              path?: unknown
              extensions?: unknown
            }>
            message: string
            extraInfo?: unknown
          }

          Object.assign(errorDetails, {
            apolloErrorDetails: {
              networkError: apolloError.networkError
                ? {
                    message: apolloError.networkError.message,
                    name: apolloError.networkError.name,
                    statusCode: apolloError.networkError.statusCode,
                    response: apolloError.networkError.response
                  }
                : null,
              graphQLErrors: apolloError.graphQLErrors
                ? apolloError.graphQLErrors.map((err) => ({
                    message: err.message,
                    locations: err.locations,
                    path: err.path,
                    extensions: err.extensions
                  }))
                : [],
              message: apolloError.message,
              extraInfo: apolloError.extraInfo
            }
          })
        }

        // Check for specific error patterns
        if (error instanceof Error) {
          if (error.message.includes('Failed to fetch')) {
            errorDetails.likelyIssue =
              'Network connectivity or server rejection'
          } else if (error.message.includes('timeout')) {
            errorDetails.likelyIssue =
              'Request timeout - file too large or server slow'
          } else if (error.message.includes('413')) {
            errorDetails.likelyIssue =
              'Payload too large - file exceeds server limits'
          } else if (error.message.includes('400')) {
            errorDetails.likelyIssue =
              'Bad request - invalid file format or metadata'
          } else if (
            error.message.includes('401') ||
            error.message.includes('403')
          ) {
            errorDetails.likelyIssue = 'Authentication/authorization issue'
          }
        }

        console.error(errorMsg, errorDetails)
      } finally {
        // Remove file from uploading list
        const index = currentUploadingFiles.indexOf(file.name)
        if (index > -1) {
          currentUploadingFiles.splice(index, 1)
          setUploadingFiles([...currentUploadingFiles])
        }
      }
    }

    // Set any errors that occurred
    if (currentErrors.length > 0) {
      setUploadErrors(currentErrors)
    }

    // Clear uploading state
    setUploadingFiles([])

    return uploadedDocumentIds
  }

  const fileInputRef = useRef<HTMLInputElement | null>(null)

  /**
   * Validates if a file meets upload requirements
   * @param file - The file to validate
   * @returns true if file is valid, false otherwise
   */
  const isValidFile = (file: File): boolean => {
    const validTypes = [
      // Images
      'image/jpeg',
      'image/jpg',
      'image/gif',
      'image/png',
      'image/bmp',
      // Documents
      'application/pdf',
      'application/msword', // .doc
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
      'text/plain', // .txt
      'application/rtf', // .rtf
      'application/vnd.wordperfect' // .wpd
    ]

    // Also check by file extension as MIME types can be unreliable
    const validExtensions = [
      '.pdf',
      '.jpg',
      '.jpeg',
      '.gif',
      '.png',
      '.bmp',
      '.wpd',
      '.txt',
      '.rtf',
      '.doc',
      '.docx'
    ]

    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf('.'))
    const isValidByType = validTypes.includes(file.type)
    const isValidByExtension = validExtensions.includes(fileExtension)

    if (!isValidByType && !isValidByExtension) {
      console.error(
        `File ${file.name} is not a valid file type. Allowed types: PDF, JPG, JPEG, GIF, PNG, BMP, WPD, TXT, RTF, DOC, DOCX`
      )
      return false
    }

    if (file.size > MAX_FILE_SIZE) {
      console.error(`File ${file.name} exceeds the maximum size of 25MB`)
      return false
    }

    return true
  }

  /**
   * Checks if a file with the same name already exists in pending files
   * @param fileName - Name of the file to check
   * @param existingFiles - Array of currently pending files
   * @returns true if duplicate exists, false otherwise
   */
  const isDuplicateFile = (
    fileName: string,
    existingFiles: File[]
  ): boolean => {
    return existingFiles.some((file) => file.name === fileName)
  }

  /**
   * Validates batch upload limits
   * @param newFiles - Array of new files to be added
   * @param existingFiles - Array of existing pending files
   * @returns object with validation result and error message if invalid
   */
  const validateBatchLimits = (
    newFiles: File[],
    existingFiles: File[] = []
  ): { isValid: boolean; errorMessage?: string } => {
    const totalFiles = existingFiles.length + newFiles.length
    const totalSize = [...existingFiles, ...newFiles].reduce(
      (sum, file) => sum + file.size,
      0
    )

    // Check file count limit
    if (totalFiles > MAX_FILES_COUNT) {
      return {
        isValid: false,
        errorMessage: `Cannot upload more than ${MAX_FILES_COUNT} files at once. You have ${existingFiles.length} files selected and are trying to add ${newFiles.length} more.`
      }
    }

    // Check total size limit
    if (totalSize > MAX_TOTAL_SIZE) {
      const totalSizeMB = Math.round(totalSize / (1024 * 1024))
      const maxSizeMB = Math.round(MAX_TOTAL_SIZE / (1024 * 1024))
      return {
        isValid: false,
        errorMessage: `Total file size (${totalSizeMB}MB) exceeds the maximum allowed size of ${maxSizeMB}MB for batch upload.`
      }
    }

    return { isValid: true }
  }

  /**
   * Formats file size for display
   * @param bytes - File size in bytes
   * @returns Formatted size string
   */
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files
    if (!selectedFiles) return

    const filesArray = Array.from(selectedFiles)

    // First validate individual files
    const validFiles = filesArray.filter(isValidFile)

    if (validFiles.length === 0) {
      setUploadErrors([
        'No valid files selected. Please select PDF, JPG, JPEG, GIF, PNG, BMP, WPD, TXT, RTF, DOC, or DOCX files under 25MB each.'
      ])
      e.target.value = ''
      return
    }

    // Filter out duplicates
    const newFiles: File[] = []
    const duplicates: string[] = []

    validFiles.forEach((file) => {
      if (isDuplicateFile(file.name, [...pendingFiles, ...newFiles])) {
        duplicates.push(file.name)
      } else {
        newFiles.push(file)
      }
    })

    // Show errors for duplicates
    const errors: string[] = []
    if (duplicates.length > 0) {
      duplicates.forEach((name) => {
        errors.push(`Duplicate file: "${name}" is already in the upload queue`)
      })
    }

    if (newFiles.length === 0) {
      setUploadErrors(
        errors.length > 0
          ? errors
          : ['All selected files are duplicates or invalid']
      )
      e.target.value = ''
      return
    }

    // Then validate batch limits with only new files
    const batchValidation = validateBatchLimits(newFiles, pendingFiles)

    if (!batchValidation.isValid) {
      errors.push(batchValidation.errorMessage || 'Invalid batch selection')
      setUploadErrors(errors)
      e.target.value = ''
      return
    }

    // Clear any previous errors and add valid files
    if (errors.length > 0) {
      setUploadErrors(errors)
    } else {
      setUploadErrors([])
    }
    setPendingFiles((prev) => [...prev, ...newFiles])

    // Reset file input so selecting the same file again will re-trigger it
    e.target.value = ''
  }

  /**
   * Handles files selected from the modal
   */
  const handleModalFilesSelected = (selectedFiles: File[]) => {
    const validFiles = selectedFiles.filter(isValidFile)

    // Filter out duplicates
    const newFiles: File[] = []
    const duplicates: string[] = []

    validFiles.forEach((file) => {
      if (isDuplicateFile(file.name, [...pendingFiles, ...newFiles])) {
        duplicates.push(file.name)
      } else {
        newFiles.push(file)
      }
    })

    // Show errors for duplicates
    if (duplicates.length > 0) {
      const duplicateErrors = duplicates.map(
        (name) => `Duplicate file: "${name}" is already in the upload queue`
      )
      setUploadErrors(duplicateErrors)
    }

    if (newFiles.length > 0) {
      setPendingFiles((prev) => [...prev, ...newFiles])
    }
  }

  const handleDelete = (index: number) => {
    setPendingFiles(pendingFiles.filter((_, i) => i !== index))
    setUploadErrors([])
  }

  const handleDismissError = (index: number) => {
    setUploadErrors(uploadErrors.filter((_, i) => i !== index))
  }

  const handleDismissAllErrors = () => {
    setUploadErrors([])
  }

  /**
   * Handles sending the message and documents to the chat
   */
  // Common function to handle sending with file uploads
  const sendMessageWithFiles = async (messageText: string) => {
    if (!sendMessage) return

    // If there are pending files to upload, set uploading state
    if (pendingFiles.length > 0) {
      if (uploadingFiles.length > 0) {
        console.warn('Cannot send message while files are still uploading')
        return
      }
    }

    try {
      // Upload pending files first if there are any
      const uploadedDocuments =
        pendingFiles.length > 0 ? (await uploadDocuments()) || [] : []

      if (uploadedDocuments && uploadedDocuments.length > 0) {
        console.log('Uploaded documents:', uploadedDocuments)
      }

      // Send message with uploaded documents if available
      sendMessage(
        messageText,
        uploadedDocuments.length > 0 ? uploadedDocuments : undefined
      )

      // Clear the pending files and errors after sending
      setPendingFiles([])
      setUploadErrors([])
    } catch (error) {
      console.error('Error uploading files:', error)
      setUploadErrors((prev) => [
        ...prev,
        `Upload failed: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      ])
    }
  }

  const handleSendMessage = async () => {
    const trimmedValue = value.trim()
    if (trimmedValue === '' && pendingFiles.length === 0) return

    await sendMessageWithFiles(trimmedValue)
    setValue('')
  }

  // Determine if input should be disabled
  const isUploading = uploadingFiles.length > 0
  const isInputDisabled = isDisabled || isUploading
  const inputPlaceholder = isUploading
    ? 'Uploading file(s)...'
    : 'Type a question or add documents'

  return (
    <div className='InputZoneContainer'>
      <FileDropZone
        onFilesSelected={(selectedFiles: File[]) => {
          // First validate individual files
          const validFiles = selectedFiles.filter(isValidFile)

          if (validFiles.length === 0) {
            setUploadErrors([
              'No valid files selected. Please select PDF, JPG, JPEG, GIF, PNG, BMP, WPD, TXT, RTF, DOC, or DOCX files under 25MB each.'
            ])
            return
          }

          // Filter out duplicates
          const newFiles: File[] = []
          const duplicates: string[] = []

          validFiles.forEach((file) => {
            if (isDuplicateFile(file.name, [...pendingFiles, ...newFiles])) {
              duplicates.push(file.name)
            } else {
              newFiles.push(file)
            }
          })

          // Show errors for duplicates
          const errors: string[] = []
          if (duplicates.length > 0) {
            duplicates.forEach((name) => {
              errors.push(
                `Duplicate file: "${name}" is already in the upload queue`
              )
            })
          }

          if (newFiles.length === 0) {
            setUploadErrors(
              errors.length > 0
                ? errors
                : ['All selected files are duplicates or invalid']
            )
            return
          }

          // Then validate batch limits with only new files
          const batchValidation = validateBatchLimits(newFiles, pendingFiles)

          if (!batchValidation.isValid) {
            errors.push(
              batchValidation.errorMessage || 'Invalid batch selection'
            )
            setUploadErrors(errors)
            return
          }

          // Clear any previous errors and add valid files
          if (errors.length > 0) {
            setUploadErrors(errors)
          } else {
            setUploadErrors([])
          }
          setPendingFiles((prev) => [...prev, ...newFiles])
        }}
      />
      {pendingFiles.length > 0 && (
        <div className='InputZoneUploadedFiles'>
          <div className='InputZoneUploadStats'>
            <span>
              {pendingFiles.length} of {MAX_FILES_COUNT} files selected
            </span>
            <span>
              Total size:{' '}
              {formatFileSize(
                pendingFiles.reduce((sum, file) => sum + file.size, 0)
              )}{' '}
              of {formatFileSize(MAX_TOTAL_SIZE)}
            </span>
          </div>
          {pendingFiles.map((file, index) => (
            <Chip
              key={`pending-${index}`}
              className='InputZonePendingFileChip'
              variant='secondary'
              isDismissable={true}
              onRemove={() => handleDelete(index)}
            >
              {file.name} ({formatFileSize(file.size)})
            </Chip>
          ))}
        </div>
      )}
      {uploadErrors.length > 0 && (
        <div className='InputZoneErrors'>
          <div className='InputZoneErrorsHeader'>
            <span>Upload Errors</span>
            <button
              type='button'
              className='InputZoneDismissAll'
              onClick={handleDismissAllErrors}
              aria-label='Dismiss all errors'
              title='Dismiss all errors'
            >
              Clear All
            </button>
          </div>
          {uploadErrors.map((error, index) => (
            <div key={index} className='InputZoneError'>
              <span>❌ {error}</span>
              <button
                type='button'
                className='InputZoneErrorDismiss'
                onClick={() => handleDismissError(index)}
                aria-label='Dismiss error'
                title='Dismiss error'
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
      <input
        type='file'
        ref={fileInputRef}
        style={{ display: 'none' }}
        multiple
        accept='.pdf,.jpg,.jpeg,.gif,.png,.bmp,.wpd,.txt,.rtf,.doc,.docx,image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,application/rtf,application/vnd.wordperfect'
        capture='environment'
        onChange={handleFileSelect}
      />
      <div className='InputZone'>
        <ChatInput
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value)
          }}
          onSend={isInputDisabled ? undefined : handleSendMessage}
          placeholder={inputPlaceholder}
          disabled={isInputDisabled}
        />
        <Button
          className='InputZoneButton'
          onClick={() => setShowDocumentModal(true)}
          disabled={isUploading}
        >
          {isUploading ? 'Uploading...' : 'Add Document'}
        </Button>
      </div>
      {prompts && prompts.length > 0 && (
        <div className='InputZonePromptPillSuggestions'>
          {prompts.map((prompt, index) => (
            <PromptPill
              key={index}
              onClick={() => {
                if (!isInputDisabled) {
                  sendMessageWithFiles(prompt)
                }
              }}
            >
              {prompt}
            </PromptPill>
          ))}
        </div>
      )}

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={showDocumentModal}
        onClose={() => setShowDocumentModal(false)}
        onFilesSelected={handleModalFilesSelected}
        isUploading={isUploading}
      />
    </div>
  )
}

export default InputZone
